import type { JsonObject, JsonValue, WorldDocument } from "./types";

export interface VfsEntry {
  path: string;
  name: string;
  isDir: boolean;
  size?: number;
  documentId?: string;
  kind?: string;
}

export interface VfsFileContent {
  path: string;
  isMarkdown: boolean;
  content: JsonObject | string;
  documentId: string;
  revision: number;
}

/**
 * Standard folder mapping for document kinds matching Craft RPG VFS conventions
 */
export function getCanonicalFolderForKind(kind: string, fields?: JsonObject): string {
  const lowerKind = kind.toLowerCase();
  switch (lowerKind) {
    case "character":
      return "/Characters";
    case "npc":
      return "/Characters/NPC Characters";
    case "monster":
    case "creature":
      return "/Monsters";
    case "location":
    case "point_of_interest":
    case "establishment":
      return "/Locations";
    case "item":
    case "weapon":
    case "armor":
    case "shield":
    case "consumable":
    case "gear":
    case "tool": {
      const category = typeof fields?.category === "string" ? fields.category : "Gear";
      return `/Items/${category}`;
    }
    case "spell":
    case "ability":
    case "feat":
      return "/Player Options/Abilities";
    case "lore":
    case "world_lore":
      return "/Lore";
    case "rule":
      return "/Rules";
    case "gm_instructions":
      return "/GM Instructions";
    case "optional_gm_instructions":
      return "/Optional GM Instructions";
    case "gm_brain":
      return "/GM Brain";
    case "game_start":
      return "/Game Starts";
    case "campaign_state":
    case "scene_state":
    case "scene":
    case "world_clock":
    case "session_record":
      return "/Sessions";
    case "faction":
      return "/Lore/Factions";
    case "quest":
      return "/Quests";
    case "file_type":
      return "/file-types";
    default:
      return `/${capitalize(kind)}s`;
  }
}

function capitalize(s: string): string {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9_\u0E00-\u0E7F]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Derives canonical VFS path from a WorldDocument
 */
export function documentToVfsPath(doc: WorldDocument): string {
  const isMd = doc.kind === "lore" || doc.kind === "rule" || doc.kind === "gm_instructions" || doc.kind === "optional_gm_instructions" || doc.kind === "gm_brain";
  const ext = isMd ? `${slugify(doc.kind)}.md` : `${slugify(doc.kind)}.json`;

  if (doc.kind === "file_type") {
    const slug = slugify(doc.title || doc.id);
    return `/file-types/${slug}.json`;
  }

  const folder = getCanonicalFolderForKind(doc.kind, doc.fields);
  const cleanTitle = doc.title.replace(/[\\/:*?"<>|]/g, "_").trim() || doc.id;
  return `${folder}/${cleanTitle}.${ext}`;
}

/**
 * Normalizes a virtual path (resolves slashes, trims trailing slashes unless root)
 */
export function normalizeVfsPath(path: string): string {
  let cleaned = path.trim().replace(/\\/g, "/");
  if (!cleaned.startsWith("/")) cleaned = "/" + cleaned;
  cleaned = cleaned.replace(/\/+/g, "/");
  if (cleaned.length > 1 && cleaned.endsWith("/")) {
    cleaned = cleaned.slice(0, -1);
  }
  return cleaned;
}

/**
 * Finds a document by matching virtual path, ID, or slug
 */
export function findDocumentByVfsPath(
  documents: readonly WorldDocument[],
  targetPath: string
): WorldDocument | undefined {
  const normalized = normalizeVfsPath(targetPath);
  const targetLower = normalized.toLowerCase();

  // 1. Exact match on derived canonical path
  for (const doc of documents) {
    if (doc.deletedAt) continue;
    if (documentToVfsPath(doc).toLowerCase() === targetLower) return doc;
  }

  // 2. Exact match on document ID or alias
  const filename = normalized.split("/").pop() || "";
  const nameWithoutExt = filename.replace(/\.[^.]+\.(json|md)$/i, "").replace(/\.(json|md)$/i, "");
  const targetSlug = slugify(nameWithoutExt);

  for (const doc of documents) {
    if (doc.deletedAt) continue;
    if (doc.id === filename || doc.id === nameWithoutExt) return doc;
    if (slugify(doc.title) === targetSlug) return doc;
    if (doc.aliases?.some((alias) => slugify(alias) === targetSlug)) return doc;
  }

  // 3. Match filename part if path folder is generic or matches kind
  for (const doc of documents) {
    if (doc.deletedAt) continue;
    const docPath = documentToVfsPath(doc).toLowerCase();
    if (docPath.endsWith(`/${filename.toLowerCase()}`)) return doc;
  }

  return undefined;
}

/**
 * Converts a WorldDocument to representation for LLM reading
 */
export function formatDocumentForVfs(doc: WorldDocument, path: string): VfsFileContent {
  const isMd = doc.kind === "lore" || doc.kind === "rule" || doc.kind === "gm_instructions" || doc.kind === "optional_gm_instructions" || doc.kind === "gm_brain" || path.endsWith(".md");
  if (isMd) {
    const body = typeof doc.fields.body === "string" ? doc.fields.body : doc.summary || "";
    return {
      path,
      isMarkdown: true,
      content: body,
      documentId: doc.id,
      revision: doc.revision,
    };
  }

  // Structured JSON envelope matching Craft file types
  const jsonContent: JsonObject = {
    name: doc.title,
    ...(doc.summary ? { summary: doc.summary } : {}),
    ...doc.fields,
    ...(doc.runtimeState && Object.keys(doc.runtimeState).length ? { runtimeState: doc.runtimeState } : {}),
    ...(doc.references && doc.references.length ? { references: doc.references as unknown as JsonValue } : {}),
  };

  return {
    path,
    isMarkdown: false,
    content: jsonContent,
    documentId: doc.id,
    revision: doc.revision,
  };
}

/**
 * Lists virtual folder contents like `ls /Characters/`
 */
export function vfsListDirectory(
  documents: readonly WorldDocument[],
  dirPath: string
): VfsEntry[] {
  const normalizedDir = normalizeVfsPath(dirPath);
  const dirPrefix = normalizedDir === "/" ? "/" : `${normalizedDir}/`;
  const subDirs = new Set<string>();
  const files: VfsEntry[] = [];

  for (const doc of documents) {
    if (doc.deletedAt) continue;
    const filePath = documentToVfsPath(doc);
    if (!filePath.startsWith(dirPrefix)) continue;

    const remaining = filePath.slice(dirPrefix.length);
    const slashIdx = remaining.indexOf("/");
    if (slashIdx >= 0) {
      const subDirName = remaining.slice(0, slashIdx);
      subDirs.add(subDirName);
    } else {
      const content = JSON.stringify(doc.fields);
      files.push({
        path: filePath,
        name: remaining,
        isDir: false,
        size: content.length,
        documentId: doc.id,
        kind: doc.kind,
      });
    }
  }

  const entries: VfsEntry[] = [];
  for (const dirName of Array.from(subDirs).sort()) {
    entries.push({
      path: normalizedDir === "/" ? `/${dirName}` : `${normalizedDir}/${dirName}`,
      name: dirName,
      isDir: true,
    });
  }

  files.sort((a, b) => a.name.localeCompare(b.name));
  return [...entries, ...files];
}

/**
 * Grep across virtual file contents in the store
 */
export function vfsGrep(
  documents: readonly WorldDocument[],
  query: string,
  searchDir = "/"
): Array<{ path: string; matches: string[]; documentId: string }> {
  const normalizedDir = normalizeVfsPath(searchDir);
  const dirPrefix = normalizedDir === "/" ? "/" : `${normalizedDir}/`;
  const lowerQuery = query.toLowerCase();
  const results: Array<{ path: string; matches: string[]; documentId: string }> = [];

  for (const doc of documents) {
    if (doc.deletedAt) continue;
    const filePath = documentToVfsPath(doc);
    if (normalizedDir !== "/" && !filePath.startsWith(dirPrefix)) continue;

    const matches: string[] = [];
    if (doc.title.toLowerCase().includes(lowerQuery)) {
      matches.push(`Title: ${doc.title}`);
    }
    if (doc.summary.toLowerCase().includes(lowerQuery)) {
      matches.push(`Summary: ${doc.summary}`);
    }

    const fieldsStr = JSON.stringify(doc.fields);
    if (fieldsStr.toLowerCase().includes(lowerQuery)) {
      const idx = fieldsStr.toLowerCase().indexOf(lowerQuery);
      const start = Math.max(0, idx - 40);
      const end = Math.min(fieldsStr.length, idx + query.length + 40);
      matches.push(`Fields: ...${fieldsStr.slice(start, end)}...`);
    }

    if (matches.length > 0) {
      results.push({ path: filePath, matches, documentId: doc.id });
    }
  }

  return results;
}

/**
 * Infers document kind from virtual path
 */
export function inferKindFromVfsPath(path: string): string {
  const normalized = normalizeVfsPath(path);
  const lower = normalized.toLowerCase();

  if (lower.startsWith("/file-types/")) return "file_type";
  if (lower.startsWith("/characters/npc characters/")) return "npc";
  if (lower.startsWith("/characters/")) return "character";
  if (lower.startsWith("/monsters/") || lower.startsWith("/creatures/")) return "monster";
  if (lower.startsWith("/locations/") || lower.startsWith("/establishments/")) return "location";
  if (lower.startsWith("/lore/factions/")) return "faction";
  if (lower.startsWith("/lore/")) return "lore";
  if (lower.startsWith("/rules/")) return "rule";
  if (lower.startsWith("/gm instructions/")) return "gm_instructions";
  if (lower.startsWith("/optional gm instructions/")) return "optional_gm_instructions";
  if (lower.startsWith("/gm brain/")) return "gm_brain";
  if (lower.startsWith("/game starts/")) return "game_start";
  if (lower.startsWith("/quests/")) return "quest";
  if (lower.startsWith("/sessions/")) {
    if (lower.includes("campaign-state") || lower.includes("campaign state")) return "campaign_state";
    if (lower.includes("scene-state") || lower.includes("scene state")) return "scene_state";
    return "session_record";
  }
  if (lower.startsWith("/items/")) {
    if (lower.includes(".weapon.")) return "weapon";
    if (lower.includes(".armor.")) return "armor";
    if (lower.includes(".shield.")) return "shield";
    return "item";
  }

  const parts = normalized.split(".");
  if (parts.length >= 3) {
    return parts[parts.length - 2]!.replace(/-/g, "_");
  }
  return "custom";
}

/**
 * Maximum token ceiling for any single instruction file (approx 5,000 tokens = ~17,000 characters).
 * Prevents giant monolithic instructions from consuming the model's entire context window.
 */
export const INSTRUCTION_CAPSULE_MAX_TOKENS = 5_000;
export const INSTRUCTION_CAPSULE_MAX_CHARACTERS = Math.floor(INSTRUCTION_CAPSULE_MAX_TOKENS * 3.4);

export interface InstructionSplitResult {
  needsSplit: boolean;
  totalTokensEstimate: number;
  documents: WorldDocument[];
}

/**
 * Checks an instruction text against the capsule token ceiling. If within bounds,
 * returns a single WorldDocument. If it exceeds 5,000 tokens, chunks/splits the text
 * into organized topic sections (e.g. Core Rules, World Lore, Mechanics) for VFS storage.
 */
export function partitionInstructionToVfsDocuments(
  storyId: string,
  rawText: string,
  title = "Core GM Instructions",
  at = new Date().toISOString()
): InstructionSplitResult {
  const charCount = rawText.trim().length;
  const tokensEstimate = Math.ceil(charCount / 3.4);

  if (tokensEstimate <= INSTRUCTION_CAPSULE_MAX_TOKENS) {
    return {
      needsSplit: false,
      totalTokensEstimate: tokensEstimate,
      documents: [{
        id: `instruction:${slugify(title)}`,
        storyId,
        kind: "gm_instructions",
        schemaVersion: 1,
        revision: 1,
        title,
        summary: rawText.slice(0, 240),
        fields: { body: rawText.trim() },
        references: [],
        visibility: "pinned",
        displayVisibility: "creator",
        editableBy: ["creator"],
        createdAt: at,
        updatedAt: at,
      }],
    };
  }

  // Exceeds capsule budget: Split into logical sections by markdown headings or paragraphs
  const rawSections = rawText.split(/\n(?=#{1,3}\s+)/g);
  const documents: WorldDocument[] = [];
  let chunkIndex = 1;

  for (const section of rawSections) {
    const trimmed = section.trim();
    if (!trimmed) continue;
    const firstLine = trimmed.split("\n")[0]?.replace(/^#+\s*/, "").trim() || `Section ${chunkIndex}`;
    const sectionTitle = `${title} - ${firstLine.slice(0, 48)}`;
    const isPrimary = chunkIndex === 1;

    documents.push({
      id: `instruction:${slugify(title)}-part-${chunkIndex}`,
      storyId,
      kind: isPrimary ? "gm_instructions" : "optional_gm_instructions",
      schemaVersion: 1,
      revision: 1,
      title: sectionTitle,
      summary: trimmed.slice(0, 240),
      fields: { body: trimmed },
      references: [],
      visibility: isPrimary ? "pinned" : "searchable",
      displayVisibility: "creator",
      editableBy: ["creator"],
      createdAt: at,
      updatedAt: at,
    });
    chunkIndex += 1;
  }

  return {
    needsSplit: true,
    totalTokensEstimate: tokensEstimate,
    documents,
  };
}

